/**
 * Edit Organization Form Composer
 *
 * Form for editing existing organizations.
 * - Pre-filled fields with organization data
 * - Uses upsert pattern with orgNumber as business key
 * - Form metadata loaded from local form.yml
 */

import * as React from 'react'
import { useAppForm } from '@/hooks/app-form'
import { useApiMutation, useApiQuery } from '@/api/ApiQueryServiceProvider'
import {
  EditOrganizationFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type EditOrganizationFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import { PhoneInputField } from '@/components/ui/phone-input'
import * as renderFunctions from '../../formRenderFunctions'
import type { ControlConfig } from '../../formRenderFunctions'
import type { Organization } from '@/types/organizations'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Building } from 'lucide-react'
import { AppLogger } from '@/lib/logger'
import { validateOrgCode } from '@/lib/utils'
import { useToast } from '@/contexts/ToastContext'

const log = AppLogger.getLogger(import.meta)

const {
  renderTextControl,
  renderNumberControl,
  renderCheckboxControl,
  renderButtonControl,
  renderSelectControl,
} = renderFunctions

// ============================================================================
// Component Props
// ============================================================================

export type EditOrganizationComposerProps = {
  organizationId: string
  initialValues: Organization
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  showHeader?: boolean
  submitLabel?: string
}

// ============================================================================
// Field validation via Zod schema (onBlur only)
// ============================================================================

const validateField = (
  name: keyof EditOrganizationFormData,
  value: unknown,
): string | null => {
  const shape = EditOrganizationFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

// ============================================================================
// Composer Component
// ============================================================================

export function EditOrganizationComposer(props: EditOrganizationComposerProps) {
  const {
    initialValues,
    onSuccess,
    onError,
    onCancel,
    showHeader = true,
    submitLabel,
  } = props
  const { toast } = useToast()

  // Get field configurations from YAML
  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const isRequiredField = (name: string) => {
    // type/role are optional (backend only requires name, orgCode, country, emailId).
    return ['name', 'orgCode', 'country', 'emailId'].includes(name)
  }

  // Fetch users for Account Manager dropdown
  const { data: usersData } = useApiQuery('getUsers', {}, {})

  // Transform users data to options
  // API returns { users: [...], total: number }, not a direct array
  // Backend may use emailAddress, emailId, or email field
  const userOptions = React.useMemo(() => {
    const users = usersData?.content?.users
    if (!users || !Array.isArray(users)) {
      return []
    }
    return users.map((user: any) => {
      const email = user.emailAddress || user.emailId || user.email || ''
      return {
        // users are identified by userNumber (there is no `id` field on the
        // getUsers response); accountManagerId is a free-form varchar on the backend
        value: String(user.userNumber),
        label: email ? `${user.firstName} ${user.lastName} (${email})` : `${user.firstName} ${user.lastName}`,
      }
    })
  }, [usersData])

  const upsertOrganization = useApiMutation('upsertOrganization', 'POST', {
    // Invalidate BOTH the list and the single-org query the detail/edit page reads from.
    // The read uses `getOrganizationByNumber`; the old 'getOrganization' key didn't exist,
    // so after a save the stale cached value kept showing (e.g. an edited taxId not reflecting).
    invalidates: ['getOrganizations', 'getOrganizationByNumber'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to update organization:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: EditOrganizationFormSchema,
    defaultValues: {
      name: initialValues.name ?? '',
      orgCode: initialValues.orgCode ?? '',
      country: initialValues.country ?? '',
      type: initialValues.type as EditOrganizationFormData['type'],
      role: initialValues.role as EditOrganizationFormData['role'],
      emailId: initialValues.emailId ?? initialValues.email ?? '',
      phone: initialValues.phone ?? '',
      // address: not stored by backend
      website: initialValues.website ?? '',
      taxId: initialValues.taxId ?? '',
      accountManagerId: initialValues.accountManagerId ?? '',
      employeeCount: initialValues.employeeCount ?? (undefined as any),
    },
    onSubmit: async ({ value }) => {
      const formValue = value as EditOrganizationFormData
      // Validate with Zod schema (single source of truth)
      const parsed = EditOrganizationFormSchema.safeParse({
        name: formValue.name?.trim() ?? '',
        orgCode: formValue.orgCode?.trim() ?? '',
        country: formValue.country?.trim() ?? '',
        type: formValue.type,
        role: formValue.role,
        emailId: formValue.emailId?.trim() ?? '',
        phone: formValue.phone?.trim() ?? '',
        // address: not stored by backend
        website: formValue.website?.trim() ?? '',
        taxId: formValue.taxId?.trim() ?? '',
        accountManagerId: formValue.accountManagerId?.trim() ?? '',
        employeeCount: formValue.employeeCount
          ? parseInt(String(formValue.employeeCount), 10)
          : undefined,
      })

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as any, (meta) => ({
              ...meta,
              isTouched: true,
              errors: [issue.message],
            }))
          }
        }
        // Surface the failure so the user sees WHY the form didn't submit (was silent).
        const first = parsed.error.issues[0]
        toast({
          title: 'Cannot save organization',
          description: first
            ? `${String(first.path[0] ?? 'Field')}: ${first.message}`
            : 'Please fix the highlighted fields.',
          type: 'error',
        })
        log.error('[EditOrganization] validation failed:', parsed.error.issues)
        return
      }

      // DB stores org_code as varchar(4); enforce the fixed 4-char length before submit.
      const orgCodeError = validateOrgCode(parsed.data.orgCode)
      if (orgCodeError) {
        form.setFieldMeta('orgCode', (meta) => ({ ...meta, isTouched: true, errors: [orgCodeError] }))
        toast({ title: 'Invalid org code', description: orgCodeError, type: 'error' })
        return
      }

      // Transform and submit with orgNumber as business key for upsert
      const apiRequest = transformToApiRequest(parsed.data, initialValues.orgNumber)
      await upsertOrganization.mutateAsync(apiRequest)
    },
  })

  // Render a single control with explicit render function
  const renderControl = (control: any) => {
    const isRequired = isRequiredField(control.name)

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          // Phone validates live (onChange) too — PhoneInputField emits its value via
          // onChange, so without this a short/invalid number (e.g. one digit) stays
          // silent because the onBlur validator alone rarely fires for this control.
          onChange:
            control.name === 'phone'
              ? ({ value }) => validateField('phone', value) || undefined
              : undefined,
          onBlur: ({ value }) =>
            validateField(control.name as keyof EditOrganizationFormData, value)
            || (control.name === 'orgCode' ? validateOrgCode(value) : null)
            || undefined,
        }}
      >
        {(bound) => {
          const baseConfig: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
          }

          // Render based on htmlControl type
          if (control.htmlControl === 'phone') {
            return (
              <PhoneInputField
                label={control.label}
                value={(bound.state.value as string) ?? ''}
                onChange={(val) => bound.handleChange(val ?? '')}
                onBlur={bound.handleBlur}
                required={isRequired}
                disabled={control.disabled ?? false}
                error={bound.state.meta.errors?.[0] as string | undefined}
              />
            )
          }

          if (control.htmlControl === 'select') {
            // Special handling for Account Manager field
            if (control.name === 'accountManagerId') {
              return renderSelectControl(
                {
                  ...baseConfig,
                  options: userOptions,
                },
                bound,
              )
            }

            // Regular select for type and role
            return renderSelectControl(
              {
                ...baseConfig,
                options: control.selectOptions || [],
              },
              bound,
            )
          }

          if (control.htmlControl === 'checkbox') {
            return renderCheckboxControl(baseConfig, bound)
          }

          if (control.htmlControl === 'input' && control.inputType === 'number') {
            return renderNumberControl(baseConfig, bound)
          }

          // Default to text control
          return renderTextControl(baseConfig, bound)
        }}
      </form.AppField>
    )
  }

  // Render form content with groups
  const renderFormContent = () => {
    return groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []

      if (groupControls.length === 0) return null

      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map(renderControl)}
        </div>
      )
    })
  }

  const formBody = (
    <>
      {renderFormContent()}

      <div className="pt-4 flex justify-center gap-3">
        {renderButtonControl({
          label: FORM_CONFIG.cancelButton?.label ?? '',
          variant: 'outline',
          onClick: onCancel,
          visibility: !!onCancel,
          type: 'button',
        })}

        {renderButtonControl(
          {
            label: upsertOrganization.isPending
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? '')
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? ''),
            variant: 'default',
            type: 'submit',
          },
          { SubmitButton: form.SubmitButton },
        )}
      </div>
    </>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Building className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Edit Organization</CardTitle>
                  <CardDescription>Update organization details below.</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="space-y-6 pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">{formBody}</div>
      )}
    </form.AppForm>
  )
}

// Re-exports from local schema
export {
  EditOrganizationFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type EditOrganizationFormData,
} from './schema'
